import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler,
} from 'chart.js';
import { Bar, Pie, Line, Doughnut, PolarArea, Radar } from 'react-chartjs-2';
import { Box, Typography, CircularProgress } from '@mui/material';
import { StyledTypography } from '../styles/styles';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler,
);

export interface ChartData {
  chartType: string; // "bar", "pie", "line", "doughnut", "polarArea", "radar"
  title: string;
  description: string;
  data: {
    labels: string[];
    datasets: Array<{
      label: string;
      data: number[];
      backgroundColor?: string | string[];
      borderColor?: string | string[];
      borderWidth?: number;
      fill?: boolean;
      tension?: number;
    }>;
  };
  options?: {
    responsive?: boolean;
    maintainAspectRatio?: boolean;
    plugins?: {
      legend?: {
        display?: boolean;
        position?: 'top' | 'bottom' | 'left' | 'right';
      };
      title?: {
        display?: boolean;
        text?: string;
      };
      tooltip?: {
        enabled?: boolean;
      };
    };
    scales?: {
      x?: {
        display?: boolean;
        title?: {
          display?: boolean;
          text?: string;
        };
      };
      y?: {
        display?: boolean;
        title?: {
          display?: boolean;
          text?: string;
        };
        beginAtZero?: boolean;
      };
    };
  };
}

interface ChartRendererProps {
  data: ChartData;
  height?: string | number;
  width?: string | number;
  loading?: boolean;
  error?: string;
  hideDescription?: boolean;
}

export const chartColours = (alpha: number = 0.8) => [
  `rgba(54, 162, 235, ${alpha})`,
  `rgba(255, 99, 132, ${alpha})`,
  `rgba(255, 205, 86, ${alpha})`,
  `rgba(75, 192, 192, ${alpha})`,
  `rgba(153, 102, 255, ${alpha})`,
  `rgba(255, 159, 64, ${alpha})`,
  `rgba(199, 199, 199, ${alpha})`,
  `rgba(83, 102, 255, ${alpha})`,
];

const ChartRenderer: React.FC<ChartRendererProps> = ({
  data,
  height = '300px',
  width = '100%',
  loading = false,
  error,
  hideDescription = false,
}) => {
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height,
          width,
          backgroundColor: '#f8f9fa',
          borderRadius: 1,
        }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height,
          width,
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeaa7',
          borderRadius: 1,
          p: 2,
        }}
      >
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  if (!data || !data.data || !data.data.labels || !data.data.datasets) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height,
          width,
          backgroundColor: '#f8f9fa',
          borderRadius: 1,
          p: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Invalid chart data
        </Typography>
      </Box>
    );
  }

  // Default options for better UX
  const defaultOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
      },
      title: {
        display: false,
      },
      tooltip: {
        enabled: true,
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: false,
        },
      },
      y: {
        display: true,
        title: {
          display: false,
        },
        beginAtZero: true,
      },
    },
  };

  // Merge user options with defaults
  const mergedOptions = {
    ...defaultOptions,
    ...data.options,
    plugins: {
      ...defaultOptions.plugins,
      ...data.options?.plugins,
    },
    scales: {
      ...defaultOptions.scales,
      ...data.options?.scales,
    },
  };

  // Generate colors if not provided
  const generateColors = (count: number, alpha: number = 0.8) => {
    const result = [];
    const colours = chartColours(alpha);
    for (let i = 0; i < count; i++) {
      result.push(colours[i % colours.length]);
    }
    return result;
  };

  // Process datasets to ensure they have colors
  const processedDatasets = data.data.datasets.map((dataset) => {
    const colors = generateColors(data.data.labels.length);
    return {
      ...dataset,
      backgroundColor: dataset.backgroundColor || colors,
      borderColor: dataset.borderColor || colors.map((color) => color.replace(/[\d.]+\)$/, '1)')),
      borderWidth: dataset.borderWidth || 1,
    };
  });

  const chartData = {
    labels: data.data.labels,
    datasets: processedDatasets,
  };

  const renderChart = () => {
    const commonProps = {
      data: chartData,
      options: mergedOptions,
      height,
      width,
    };

    switch (data.chartType.toLowerCase()) {
      case 'bar':
        return <Bar {...commonProps} />;
      case 'pie':
        return <Pie {...commonProps} />;
      case 'line':
        return <Line {...commonProps} />;
      case 'doughnut':
        return <Doughnut {...commonProps} />;
      case 'polararea':
        return <PolarArea {...commonProps} />;
      case 'radar':
        return <Radar {...commonProps} />;
      default:
        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height,
              width,
              backgroundColor: '#f8f9fa',
              borderRadius: 1,
              p: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Unsupported chart type: {data.chartType}
            </Typography>
          </Box>
        );
    }
  };

  return (
    <Box sx={{ width, height }}>
      {data.description && !hideDescription && (
        <StyledTypography
          sx={{
            mb: 2,
            fontSize: '0.875rem',
            color: 'text.secondary',
            fontStyle: 'italic',
          }}
        >
          {data.description}
        </StyledTypography>
      )}
      <Box
        sx={{
          position: 'relative',
          height: typeof height === 'string' ? height : `${height}px`,
          width: typeof width === 'string' ? width : `${width}px`,
        }}
      >
        {renderChart()}
      </Box>
    </Box>
  );
};

export default ChartRenderer;
